import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const ContactForm = () => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const contactSchema = z.object({
    name: z.string().trim().min(1, t('contactForm.nameRequired')).max(100, t('contactForm.nameTooLong')),
    email: z.string().trim().email(t('contactForm.invalidEmail')).max(255, t('contactForm.emailTooLong')),
    message: z.string().trim().min(1, t('contactForm.messageRequired')).max(1000, t('contactForm.messageTooLong')),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    try {
      contactSchema.parse({ name, email, message });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: t('contactForm.validationError'),
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: { name, email, message },
      });

      if (error) throw error;

      toast({
        title: t('contactForm.successTitle'),
        description: t('contactForm.successDesc'),
      });

      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      console.error("Error sending contact form:", error);
      toast({
        title: t('contactForm.errorTitle'),
        description: t('contactForm.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Input
          type="text"
          placeholder={t('contactForm.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="bg-background border-border focus:border-secondary transition-colors font-body text-lg"
        />
      </div>
      
      <div>
        <Input
          type="email"
          placeholder={t('contactForm.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-background border-border focus:border-secondary transition-colors font-body text-lg"
        />
      </div>
      
      <div>
        <Textarea
          placeholder={t('contactForm.messagePlaceholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          rows={6}
          className="bg-background border-border focus:border-secondary transition-colors resize-none font-body text-lg"
        />
      </div>
      
      <Button 
        type="submit" 
        size="lg"
        className="w-full font-display text-lg py-6"
        disabled={isLoading}
      >
        {isLoading ? t('contactForm.sending') : t('contactForm.send')}
      </Button>
    </form>
  );
};